import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';
import { cn } from '@/lib/utils';
import { X, Sparkles, Calendar, Star, ChevronRight, Zap, CalendarDays, Repeat } from 'lucide-react';

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

export type OfferTheme = 'monthly' | 'weekly' | 'daily' | 'one_time';

interface ThemeConfig {
  bgGradient: string;
  borderColor: string;
  glowShadow: string;
  sparkleColors: [string, string];
  headerGradient: string;
  title: string;
  subtitle: string;
  icon: typeof Calendar;
  iconBgGradient: string;
  buttonGradient: string;
  buttonHoverGradient: string;
  buttonTextColor: string;
  buttonBorderColor: string;
  cardBgGradient: string;
  cardBorderColor: string;
  selectedItemGradient: string;
  selectedItemRing: string;
  selectedItemShadow: string;
  unselectedItemBg: string;
  unselectedItemBorder: string;
  accentColor: string;
  footerTextColor: string;
}

const THEME_CONFIGS: Record<OfferTheme, ThemeConfig> = {
  monthly: {
    bgGradient: 'from-violet-900 via-purple-900 to-violet-950',
    borderColor: 'border-purple-400/60',
    glowShadow: 'shadow-[0_0_60px_rgba(167,139,250,0.4)]',
    sparkleColors: ['text-purple-300', 'text-violet-300'],
    headerGradient: 'from-purple-200 via-violet-100 to-purple-200',
    title: 'MONTHLY SPECIAL',
    subtitle: 'Your VIP reward is here — claim it now!',
    icon: CalendarDays,
    iconBgGradient: 'from-purple-400 to-violet-500',
    buttonGradient: 'from-purple-400 via-violet-400 to-purple-400',
    buttonHoverGradient: 'hover:from-purple-300 hover:via-violet-300 hover:to-purple-300',
    buttonTextColor: 'text-violet-900',
    buttonBorderColor: 'border-purple-300/50',
    cardBgGradient: 'from-violet-800/50 to-violet-900/50',
    cardBorderColor: 'border-violet-500/30',
    selectedItemGradient: 'from-purple-400 to-violet-400',
    selectedItemRing: 'ring-purple-300',
    selectedItemShadow: 'shadow-purple-500/40',
    unselectedItemBg: 'bg-violet-900/60',
    unselectedItemBorder: 'border-violet-500/40',
    accentColor: 'text-purple-400/60',
    footerTextColor: 'text-purple-300/60',
  },
  weekly: {
    bgGradient: 'from-blue-900 via-cyan-900 to-blue-950',
    borderColor: 'border-cyan-400/60',
    glowShadow: 'shadow-[0_0_60px_rgba(34,211,238,0.4)]',
    sparkleColors: ['text-cyan-300', 'text-blue-300'],
    headerGradient: 'from-cyan-200 via-blue-100 to-cyan-200',
    title: 'WEEKLY DEAL',
    subtitle: "This week's hottest offer — grab it before it's gone!",
    icon: Repeat,
    iconBgGradient: 'from-cyan-400 to-blue-500',
    buttonGradient: 'from-cyan-400 via-blue-400 to-cyan-400',
    buttonHoverGradient: 'hover:from-cyan-300 hover:via-blue-300 hover:to-cyan-300',
    buttonTextColor: 'text-blue-900',
    buttonBorderColor: 'border-cyan-300/50',
    cardBgGradient: 'from-blue-800/50 to-blue-900/50',
    cardBorderColor: 'border-blue-500/30',
    selectedItemGradient: 'from-cyan-400 to-blue-400',
    selectedItemRing: 'ring-cyan-300',
    selectedItemShadow: 'shadow-cyan-500/40',
    unselectedItemBg: 'bg-blue-900/60',
    unselectedItemBorder: 'border-blue-500/40',
    accentColor: 'text-cyan-400/60',
    footerTextColor: 'text-cyan-300/60',
  },
  daily: {
    bgGradient: 'from-emerald-900 via-green-900 to-emerald-950',
    borderColor: 'border-emerald-400/60',
    glowShadow: 'shadow-[0_0_60px_rgba(52,211,153,0.4)]',
    sparkleColors: ['text-emerald-300', 'text-green-300'],
    headerGradient: 'from-emerald-200 via-green-100 to-emerald-200',
    title: 'TODAY ONLY',
    subtitle: "24 hours to unlock this deal — don't wait!",
    icon: Calendar,
    iconBgGradient: 'from-emerald-400 to-green-500',
    buttonGradient: 'from-emerald-400 via-green-400 to-emerald-400',
    buttonHoverGradient: 'hover:from-emerald-300 hover:via-green-300 hover:to-emerald-300',
    buttonTextColor: 'text-green-900',
    buttonBorderColor: 'border-emerald-300/50',
    cardBgGradient: 'from-emerald-800/50 to-emerald-900/50',
    cardBorderColor: 'border-emerald-500/30',
    selectedItemGradient: 'from-emerald-400 to-green-400',
    selectedItemRing: 'ring-emerald-300',
    selectedItemShadow: 'shadow-emerald-500/40',
    unselectedItemBg: 'bg-emerald-900/60',
    unselectedItemBorder: 'border-emerald-500/40',
    accentColor: 'text-emerald-400/60',
    footerTextColor: 'text-emerald-300/60',
  },
  one_time: {
    bgGradient: 'from-rose-900 via-pink-900 to-rose-950',
    borderColor: 'border-rose-400/60',
    glowShadow: 'shadow-[0_0_60px_rgba(251,113,133,0.4)]',
    sparkleColors: ['text-rose-300', 'text-pink-300'],
    headerGradient: 'from-rose-200 via-pink-100 to-rose-200',
    title: 'RARE OFFER',
    subtitle: 'This deal will never come back — seize it now!',
    icon: Zap,
    iconBgGradient: 'from-rose-400 to-pink-500',
    buttonGradient: 'from-rose-400 via-pink-400 to-rose-400',
    buttonHoverGradient: 'hover:from-rose-300 hover:via-pink-300 hover:to-rose-300',
    buttonTextColor: 'text-pink-900',
    buttonBorderColor: 'border-rose-300/50',
    cardBgGradient: 'from-rose-800/50 to-rose-900/50',
    cardBorderColor: 'border-rose-500/30',
    selectedItemGradient: 'from-rose-400 to-pink-400',
    selectedItemRing: 'ring-rose-300',
    selectedItemShadow: 'shadow-rose-500/40',
    unselectedItemBg: 'bg-rose-900/60',
    unselectedItemBorder: 'border-rose-500/40',
    accentColor: 'text-rose-400/60',
    footerTextColor: 'text-rose-300/60',
  },
};

interface ScheduledOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: ScheduledOffer | null;
  offerGroup: ScheduledOfferGroup | null;
  hasMoreOffers: boolean;
  onPurchase: (offer: ScheduledOffer) => void;
  theme: OfferTheme;
}

const formatCoins = (amount: number): string => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(0)}K`;
  }
  return amount.toString();
};

const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

export const ScheduledOfferModal = ({
  isOpen,
  onClose,
  offer,
  offerGroup,
  hasMoreOffers,
  onPurchase,
  theme,
}: ScheduledOfferModalProps) => {
  const config = THEME_CONFIGS[theme];
  const IconComponent = config.icon;
  
  const isGroupOffer = offerGroup !== null;
  
  const groupOffers = useMemo(() => {
    if (!offerGroup) return [];
    return [...offerGroup.offers]
      .filter(o => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0)
      .sort((a, b) => a.price - b.price)
      .slice(0, 6);
  }, [offerGroup]);
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const selectedOffer = isGroupOffer ? groupOffers[selectedIndex] : offer;
  
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen, offerGroup, offer]);
  
  if (!selectedOffer) return null;
  
  const totalGC = selectedOffer.gc_amount + selectedOffer.gc_bonus_amount;
  const totalSC = selectedOffer.sc_amount + selectedOffer.sc_bonus_amount;
  const hasDiscount = selectedOffer.original_price && selectedOffer.original_price > selectedOffer.price;
  
  const gcBonusPercent = selectedOffer.gc_amount > 0 
    ? Math.round((selectedOffer.gc_bonus_amount / selectedOffer.gc_amount) * 100) 
    : 0;
  const scBonusPercent = selectedOffer.sc_amount > 0 
    ? Math.round((selectedOffer.sc_bonus_amount / selectedOffer.sc_amount) * 100) 
    : 0;
  const bonusPercent = Math.max(gcBonusPercent, scBonusPercent);

  const handlePurchase = () => {
    onPurchase(selectedOffer);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-lg w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className={cn(
          "relative rounded-2xl overflow-hidden border-2",
          `bg-gradient-to-b ${config.bgGradient}`,
          config.borderColor,
          config.glowShadow
        )}>
          {/* Animated background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[60%] bg-gradient-radial from-white/20 via-white/5 to-transparent" />
            
            <Sparkles className={cn("absolute top-8 left-8 h-6 w-6 animate-pulse", config.sparkleColors[0])} />
            <Sparkles className={cn("absolute top-12 right-10 h-5 w-5 animate-pulse", config.sparkleColors[1])} style={{ animationDelay: '0.3s' }} />
            <Sparkles className={cn("absolute bottom-32 left-6 h-4 w-4 animate-pulse", config.sparkleColors[0])} style={{ animationDelay: '0.6s' }} />
            <Sparkles className={cn("absolute bottom-24 right-8 h-5 w-5 animate-pulse", config.sparkleColors[1])} style={{ animationDelay: '0.9s' }} />
            <Star className={cn("absolute top-20 left-1/4 h-3 w-3 animate-twinkle", config.sparkleColors[0], "opacity-60")} />
            <Star className={cn("absolute top-16 right-1/4 h-4 w-4 animate-twinkle", config.sparkleColors[1], "opacity-60")} style={{ animationDelay: '0.5s' }} />
            
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors border border-white/20"
          >
            <X className="h-5 w-5 text-white" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className={cn(
                "inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 shadow-lg",
                `bg-gradient-to-br ${config.iconBgGradient}`,
                config.glowShadow.replace('60px', '30px')
              )}>
                <IconComponent className="h-8 w-8 text-white" />
              </div>
              <h2 className={cn(
                "text-2xl sm:text-3xl font-black text-transparent bg-clip-text mb-2",
                `bg-gradient-to-r ${config.headerGradient}`
              )}>
                {config.title}
              </h2>
              <p className={cn("text-sm sm:text-base", config.footerTextColor.replace('/60', '/80'))}>
                {config.subtitle}
              </p>
            </div>

            {/* Bonus badge */}
            {bonusPercent > 0 && (
              <div className="flex items-center justify-center mb-6">
                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 text-white font-black text-sm shadow-lg animate-pulse">
                  +{bonusPercent}% BONUS
                </div>
              </div>
            )}

            {/* Group offers - Option selector */}
            {isGroupOffer && groupOffers.length > 1 && (
              <div className="mb-6">
                <p className={cn("text-center text-sm mb-3 font-medium", config.footerTextColor.replace('/60', '/80'))}>
                  Pick your perfect deal:
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {groupOffers.map((groupOffer, index) => {
                    const offerGC = groupOffer.gc_amount + groupOffer.gc_bonus_amount;
                    const offerSC = groupOffer.sc_amount + groupOffer.sc_bonus_amount;
                    const isSelected = index === selectedIndex;
                    const canBuy = groupOffer.can_purchase !== false && (groupOffer.purchases_remaining ?? 1) > 0;

                    return (
                      <button
                        key={groupOffer.id}
                        onClick={() => setSelectedIndex(index)}
                        disabled={!canBuy}
                        className={cn(
                          "w-full px-4 py-3 rounded-xl transition-all flex items-center justify-between",
                          isSelected
                            ? cn(
                                `bg-gradient-to-r ${config.selectedItemGradient}`,
                                config.buttonTextColor,
                                `ring-2 ${config.selectedItemRing}`,
                                `shadow-lg ${config.selectedItemShadow}`,
                                "scale-[1.02]"
                              )
                            : canBuy
                              ? cn(
                                  config.unselectedItemBg,
                                  "text-white/90",
                                  `hover:bg-opacity-70 border ${config.unselectedItemBorder}`
                                )
                              : "bg-muted text-muted-foreground cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <GCBadgeSVG size={20} />
                            <span className={cn("font-black text-base", isSelected ? config.buttonTextColor : "text-white/90")}>
                              {formatCoins(offerGC)}
                            </span>
                          </div>
                          {offerSC > 0 && (
                            <>
                              <span className={cn("text-sm", isSelected ? "opacity-70" : "opacity-50")}>+</span>
                              <div className="flex items-center gap-1.5">
                                <SCBadgeSVG size={20} />
                                <span className={cn("font-bold text-sm", isSelected ? "text-green-700" : "text-green-400")}>
                                  {formatCoins(offerSC)}
                                </span>
                                <span className={cn("text-xs font-semibold", isSelected ? "text-green-700" : "text-green-400/80")}>
                                  FREE
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        <div className={cn(
                          "font-black text-base px-3 py-1 rounded-lg",
                          isSelected 
                            ? "bg-black/20" 
                            : "bg-black/30 text-white/90"
                        )}>
                          {formatPrice(groupOffer.price)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Single offer display */}
            {(!isGroupOffer || groupOffers.length === 1) && (
              <div className={cn(
                "rounded-2xl p-6 mb-6 border",
                `bg-gradient-to-br ${config.cardBgGradient}`,
                config.cardBorderColor
              )}>
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center gap-3">
                    <GCBadgeSVG size={32} />
                    <span className={cn(
                      "text-5xl font-black text-transparent bg-clip-text",
                      `bg-gradient-to-r ${config.headerGradient}`
                    )}>
                      {formatCoins(totalGC)}
                    </span>
                  </div>
                  
                  {totalSC > 0 && (
                    <div className="flex items-center gap-3">
                      <span className={cn("text-2xl", config.accentColor)}>+</span>
                      <SCBadgeSVG size={32} />
                      <span className="text-4xl font-black text-green-400">
                        {formatCoins(totalSC)}
                      </span>
                      <span className="text-green-400/70 text-xl font-bold">FREE</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchase button */}
            <Button
              onClick={handlePurchase}
              disabled={!selectedOffer.can_purchase}
              className={cn(
                "w-full h-14 sm:h-16 rounded-xl text-lg sm:text-xl font-black relative overflow-hidden group",
                `bg-gradient-to-r ${config.buttonGradient}`,
                config.buttonHoverGradient,
                config.buttonTextColor,
                "shadow-lg",
                `border-2 ${config.buttonBorderColor}`,
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                {hasDiscount && selectedOffer.original_price && (
                  <span className="line-through opacity-60 text-base">
                    {formatPrice(selectedOffer.original_price)}
                  </span>
                )}
                <span className="text-2xl">{formatPrice(selectedOffer.price)}</span>
                <ChevronRight className="h-6 w-6" />
              </span>
              
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </Button>

            {/* Footer notice */}
            <p className={cn("text-center text-xs mt-4", config.footerTextColor)}>
              {hasMoreOffers 
                ? "More exclusive deals waiting for you!" 
                : theme === 'one_time' 
                  ? "Once it's gone, it's gone forever!"
                  : theme === 'daily'
                    ? "Expires at midnight — act fast!"
                    : theme === 'weekly'
                      ? "Resets in a few days — don't miss out!"
                      : "Available this month only!"
              }
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
